import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import {
  PLATFORM_LINK,
  PERSONAL_LINK,
  copyLink
} from '../../../helpers/helperFunctions';
import {
  ACCOUNTING,
  HELPDESK,
  LOGIN,
  SETTINGS_ACCOUNT,
  SETTINGS_LAYOUT,
  SUPPORT_TICKETS
} from '../../../routes/routes';
import {
  Flexed,
  SectionSeparator,
  UserImageCircle
} from '../../../styles/GlobalComponents';
import { colors } from '../../../styles/GlobalStyles';
import {
  Icon as Icn,
  Icon,
  IconAccounting,
  IconCopy,
  IconHelpdesk,
  IconInviteLinks,
  IconLogout,
  IconSettings
} from '../../../styles/Icons';
import { FlexCenter } from '../../../styles/stylesStyled';
import { UserInfoType } from '../../../types/loginTypes';
import { useWindowWide } from '../../../helpers/wideScreen';
import {
  Collapsable,
  Menu,
  MenuInviteLabels,
  MenuInvitePlaceholder
} from '../BackofficeLayoutStyled';
import { useAppSelector } from '../../../app/hooks';

const Overlay = styled.div<{ open: boolean }>`
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(28, 23, 22, 0.1);
  backdrop-filter: blur(5px);
  visibility: ${(props) => (props.open ? 'visible' : 'hidden')};
  opacity: ${(props) => (props.open ? '1' : '0')};
  z-index: 100999 !important;
  transition: all 0.5s ease;
  height: 100vh;
`;

const SidebarContainer = styled.div<{ open: boolean }>`
  display: flex;
  flex-direction: column;
  width: 300px;
  max-width: 100%;
  padding: 40px 20px;
  position: fixed;
  right: ${({ open }) => (open ? '0' : '-350px')};
  top: 0;
  bottom: 0;
  background: ${(props) => props.theme.appLayout.sideNavBackground};
  box-shadow: ${({ theme }) => theme.shadows.shadowXS};

  transition: all 0.3s ease;
  z-index: 4999999 !important;

  font-weight: bold;
  font-size: 16px;
  line-height: 20px;
  overflow: auto;
  height: 100vh;

  span {
    font-weight: normal;
    font-size: 14px;
    line-height: 20px;
  }
`;

interface ProfileDropdownType {
  menuIsOpen: boolean;
  userInfo: UserInfoType;
  setMenuIsOpen: Dispatch<SetStateAction<boolean>>;
}

export const ProfileDropdown = ({
  menuIsOpen,
  userInfo,
  setMenuIsOpen
}: ProfileDropdownType) => {
  const { t } = useTranslation('layout');
  const history = useHistory();

  const { isCanadaRestrictedUser } = useAppSelector(
    (state) => state.auth.userInfo
  );

  const [inviteIsOpened, setInviteIsOpened] = useState(false);

  const referralUrl = `${PLATFORM_LINK}/ambassador/${userInfo?.user?.referralCode}/${userInfo?.user?.id}`;
  const personalUrl = `${PERSONAL_LINK}?uid=${userInfo?.user?.id}&h=${userInfo?.user?.referralCode}`;

  const wide = useWindowWide(800);

  const handleLogout = () => {
    history.push(LOGIN);
    setMenuIsOpen(false);
  };

  const MenuContent = () => {
    return (
      <div>
        <FlexCenter>
          <UserImageCircle
            image={userInfo?.user?.profileImageUrl}
            name={userInfo?.user?.name}
            surname={userInfo?.user?.surname}
            size='56px'
            fontSize='20px'
            margin={'0 20px 0 0'}
          />

          <div>
            <div>{userInfo?.user?.name + ' ' + userInfo?.user?.surname}</div>
            <span>
              {t('profile.id')} {userInfo?.user?.id}
            </span>
          </div>
        </FlexCenter>
        <SectionSeparator margin={'24px 0'} />

        {!isCanadaRestrictedUser && (
          <>
            <FlexCenter
              style={{ cursor: 'pointer' }}
              onClick={() => setInviteIsOpened(!inviteIsOpened)}
              className='invite-links'>
              <Icon pointer mr='16' color={colors.neutral900}>
                <IconInviteLinks />
              </Icon>
              {t('profile.invite_links')}
            </FlexCenter>

            <Collapsable expanded={inviteIsOpened}>
              {/* REFERRAL ID */}
              <Flexed between>
                <MenuInviteLabels
                  style={{ marginTop: inviteIsOpened ? 10 : 0 }}>
                  {t('profile.my_id')}
                </MenuInviteLabels>
                <Icn
                  w='20px'
                  pointer
                  mb='12'
                  color={colors.neutral900}
                  onClick={() => {
                    copyLink(
                      userInfo?.user?.id.toString(),
                      t('profile.id_has_been_copied')
                    );
                  }}>
                  <IconCopy />
                </Icn>
              </Flexed>
              <MenuInvitePlaceholder>
                {userInfo?.user?.id}
              </MenuInvitePlaceholder>

              {/* REFERRAL URL */}
              <Flexed between>
                <MenuInviteLabels>
                  {t('profile.referral_link')}
                </MenuInviteLabels>

                <Icn
                  w='20px'
                  pointer
                  mb='12'
                  color={colors.neutral900}
                  onClick={() => {
                    copyLink(
                      referralUrl,
                      t('profile.partner_link_has_been_copied')
                    );
                  }}>
                  <IconCopy />
                </Icn>
              </Flexed>
              <MenuInvitePlaceholder>{referralUrl}</MenuInvitePlaceholder>

              {/* PERSONAL URL */}

              <Flexed between>
                <MenuInviteLabels>
                  {t('profile.personal_referral_link')}
                </MenuInviteLabels>

                <Icn
                  w='20px'
                  pointer
                  mb='12'
                  color={colors.neutral900}
                  onClick={() => {
                    copyLink(
                      personalUrl,
                      t('profile.personal_link_has_been_copied')
                    );
                  }}>
                  <IconCopy />
                </Icn>
              </Flexed>

              <MenuInvitePlaceholder>{personalUrl}</MenuInvitePlaceholder>
            </Collapsable>
          </>
        )}

        <Link to={ACCOUNTING} onClick={() => setMenuIsOpen(!menuIsOpen)}>
          <FlexCenter style={{ marginBottom: 24, marginTop: 24 }}>
            <Icon pointer mr='16' color={colors.neutral900}>
              <IconAccounting />
            </Icon>
            {t('profile.accounting')}
          </FlexCenter>
        </Link>
        <Link
          to={!wide ? SETTINGS_LAYOUT : SETTINGS_ACCOUNT}
          onClick={() => setMenuIsOpen(!menuIsOpen)}>
          <FlexCenter style={{ marginBottom: 24 }}>
            <Icon pointer mr='16' color={colors.neutral900}>
              <IconSettings />
            </Icon>

            {t('profile.settings')}
          </FlexCenter>
        </Link>
        <Link to={HELPDESK} onClick={() => setMenuIsOpen(!menuIsOpen)}>
          <FlexCenter style={{ marginBottom: 24 }}>
            <Icon pointer mr='16' color={colors.neutral900}>
              <IconHelpdesk />
            </Icon>
            {t('profile.helpdesk')}
          </FlexCenter>
        </Link>

        <Link to={SUPPORT_TICKETS} onClick={() => setMenuIsOpen(!menuIsOpen)}>
          <FlexCenter style={{ marginBottom: 24 }}>
            <Icon pointer mr='16' color={colors.neutral900}>
              <IconHelpdesk />
            </Icon>
            Support Tickets
          </FlexCenter>
        </Link>

        <SectionSeparator margin={'24px 0'} />
        <FlexCenter onClick={handleLogout} style={{ cursor: 'pointer' }}>
          <Icon pointer mr='16' color={colors.neutral900}>
            <IconLogout />
          </Icon>
          {t('profile.logout')}
        </FlexCenter>
      </div>
    );
  };

  useEffect(() => {
    if (!wide && menuIsOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'scroll';
    }
  }, [menuIsOpen, wide]);

  return (
    <>
      {wide ? (
        <Menu isOpen={menuIsOpen}>
          <MenuContent />
        </Menu>
      ) : (
        <>
          <Overlay
            open={menuIsOpen}
            onClick={() => setMenuIsOpen(false)}></Overlay>
          <SidebarContainer open={menuIsOpen}>
            <MenuContent />
          </SidebarContainer>
        </>
      )}
    </>
  );
};
